/**
 * TableLifecycleFormBody — shared rename / move-to-schema form body. The form
 * surfaces two sub-modes:
 *   - mode === 'rename': name field; mysql shows an optional target-schema field
 *     for the cross-database rename idiom (`RENAME TABLE old_db.old TO new_db.new`).
 *   - mode === 'move-schema': target-schema field (mssql + postgres only).
 *
 * The owning popover seeds the `fromTable` QualifiedName by extracting the
 * parked statement's structure via `editContext.service.extractDdlStructure`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D1 — Table rename + schema-move UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitMoveTableToSchema,
  isMoveTableToSchemaSupported,
  validateMoveTableToSchemaForEngine,
} from '../../edit/edits/moveTableToSchema';
import {
  commitRenameTable,
  validateRenameTableForEngine,
} from '../../edit/edits/renameTable';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

export type TableLifecycleMode = 'rename' | 'move-schema';

export interface TableLifecycleFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: TableLifecycleMode;
  readonly fromTable: QualifiedName;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function TableLifecycleFormBody(props: TableLifecycleFormBodyProps) {
  const { editContext, mode, fromTable, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showMysqlCrossDb = mode === 'rename' && engine === 'mysql';

  const [toName, setToName] = useState(fromTable.name);
  const [toSchema, setToSchema] = useState('');
  const [mysqlTargetDb, setMysqlTargetDb] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    if (mode === 'rename') {
      const args = {
        fromTable,
        toName: toName.trim(),
        mysqlToSchema: showMysqlCrossDb && mysqlTargetDb.trim() !== ''
          ? mysqlTargetDb.trim()
          : undefined,
      };
      const validationError = validateRenameTableForEngine(engine, args);
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitRenameTable(editContext, args);
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const args = { fromTable, toSchema: toSchema.trim() };
    const validationError = validateMoveTableToSchemaForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitMoveTableToSchema(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  if (mode === 'move-schema' && !isMoveTableToSchemaSupported(engine)) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">Move to schema</div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {validateMoveTableToSchemaForEngine(engine, { fromTable, toSchema: '' }) ?? ''}
        </div>
        <div className="flex items-center justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-close`}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  const headerLabel = mode === 'rename' ? 'Rename table' : 'Move to schema';
  const submitLabel = mode === 'rename' ? 'Rename' : 'Move';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">{headerLabel}</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
        Source:{' '}
        <span className="font-mono">{fromTable.raw && fromTable.raw.length > 0 ? fromTable.raw : fromTable.name}</span>
      </div>

      {mode === 'rename' ? (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-to-name`} className="text-xs">
              New name <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${testId}-to-name`}
              data-testid={`${testId}-to-name`}
              value={toName}
              onChange={(e) => setToName(e.target.value)}
              placeholder="new_table_name"
              className="h-8 font-mono text-xs"
            />
          </div>
          {showMysqlCrossDb ? (
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${testId}-mysql-target-db`} className="text-xs">
                Target database (optional)
              </Label>
              <Input
                id={`${testId}-mysql-target-db`}
                data-testid={`${testId}-mysql-target-db`}
                value={mysqlTargetDb}
                onChange={(e) => setMysqlTargetDb(e.target.value)}
                placeholder="other_db"
                className="h-8 font-mono text-xs"
              />
              <div className="text-[10px] text-muted-foreground">
                MySQL conflates schemas with databases. Set a target database to do a cross-database rename.
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-to-schema`} className="text-xs">
            Target schema <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-to-schema`}
            data-testid={`${testId}-to-schema`}
            value={toSchema}
            onChange={(e) => setToSchema(e.target.value)}
            placeholder="archive"
            className="h-8 font-mono text-xs"
          />
        </div>
      )}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? `${submitLabel}…` : submitLabel}
        </Button>
      </div>
    </div>
  );
}
